/**
 * QSales Installation System
 *
 * Handles:
 * - Main product + installation Add to Cart
 * - Native Shopify Buy It Now + installation
 * - Installation quantity follows product quantity
 * - Product inventory / stock protection
 * - Main product removal -> linked installation removal
 * - Installation can be removed independently
 * - Multiple products using the same installation service
 * - Same product can be added separately
 * - Cart drawer synchronization
 * - Full /cart page synchronization
 *
 * QSales custom file.
 */

(() => {
  'use strict';

  if (window.__qsInstallationSystemLoaded) return;

  window.__qsInstallationSystemLoaded = true;

  let cartSyncInProgress = false;
  let cartSyncTimer = null;
  let fullCartSyncTimer = null;

  const PRODUCT_FORM_SELECTOR =
    'form[is="product-form"][data-type="add-to-cart-form"]';

  /* =========================================================
     BASIC HELPERS
     ========================================================= */

  const getRoutes = () => {
    const cartUrl =
      window.FoxTheme?.routes?.cart_url ||
      '/cart';

    return {
      add:
        window.FoxTheme?.routes?.cart_add_url ||
        '/cart/add.js',

      cart: cartUrl,

      cartJson:
        `${cartUrl.replace(/\/$/, '')}.js`,

      update:
        window.FoxTheme?.routes?.cart_update_url ||
        '/cart/update.js'
    };
  };

  const isFullCartPage = () => {
    const path =
      window.location.pathname.replace(
        /\/$/,
        ''
      );

    return (
      path === '/cart' ||
      path.endsWith('/cart')
    );
  };

  const getProductRoot = (element) =>
    element?.closest('[id^="ProductInfo-"]') ||
    element?.closest('product-info') ||
    element?.closest('.product__info-container') ||
    null;

  const getProductForm = (element) => {
    const root =
      getProductRoot(element);

    return (
      root?.querySelector(
        PRODUCT_FORM_SELECTOR
      ) || null
    );
  };

  const getInstallationOption = (
    element
  ) => {
    const root =
      getProductRoot(element);

    return (
      root?.querySelector(
        '[data-qs-installation]'
      ) || null
    );
  };

  const getInstallationCheckbox = (
    element
  ) => {
    const option =
      getInstallationOption(element);

    return (
      option?.querySelector(
        '[data-qs-installation-checkbox]'
      ) || null
    );
  };

  const getExistingProperties = (
    formData
  ) => {
    const properties = {};

    for (
      const [key, value]
      of formData.entries()
    ) {
      const match =
        key.match(
          /^properties\[(.+)\]$/
        );

      if (!match) continue;

      properties[match[1]] =
        value;
    }

    return properties;
  };

  const createGroupId = () => {
    const randomPart =
      window.crypto &&
      typeof window.crypto.randomUUID ===
        'function'
        ? window.crypto.randomUUID()
        : Math.random()
            .toString(36)
            .slice(2);

    return `qs-install-${Date.now()}-${randomPart}`;
  };

  const getSectionsToBundle = () => {
    const sections = [];

    document.documentElement.dispatchEvent(
      new CustomEvent(
        'cart:grouped-sections',
        {
          bubbles: true,
          detail: {
            sections
          }
        }
      )
    );

    return [
      ...new Set(
        sections.filter(Boolean)
      )
    ];
  };

  const showError = (
    form,
    message
  ) => {
    const errorElement =
      form.querySelector(
        '.product-form__error-message'
      );

    if (!errorElement) {
      alert(message);
      return;
    }

    errorElement.textContent =
      message;

    errorElement.hidden =
      false;
  };

  const clearError = (form) => {
    const errorElement =
      form.querySelector(
        '.product-form__error-message'
      );

    if (!errorElement) return;

    errorElement.textContent = '';
    errorElement.hidden = true;
  };

  const publishCartUpdate = (
    cart
  ) => {
    if (
      !window.FoxTheme?.pubsub ||
      !window.FoxTheme?.pubsub
        ?.PUB_SUB_EVENTS?.cartUpdate
    ) {
      return false;
    }

    window.FoxTheme.pubsub.publish(
      window.FoxTheme.pubsub
        .PUB_SUB_EVENTS.cartUpdate,
      {
        cart
      }
    );

    return true;
  };

  /* =========================================================
     INVENTORY / STOCK PROTECTION
     ========================================================= */

  const getVariantInventoryData = (
    installationOption
  ) => {
    if (!installationOption) {
      return [];
    }

    const dataElement =
      installationOption.querySelector(
        '[data-qs-variant-inventory]'
      );

    if (!dataElement) {
      return [];
    }

    try {
      const data =
        JSON.parse(
          dataElement.textContent
        );

      return Array.isArray(data)
        ? data
        : [];
    } catch (error) {
      console.error(
        'QSales Installation Inventory Data:',
        error
      );

      return [];
    }
  };

  const getVariantInventory = (
    installationOption,
    variantId
  ) => {
    const variants =
      getVariantInventoryData(
        installationOption
      );

    return (
      variants.find(
        (variant) =>
          Number(variant.id) ===
          Number(variantId)
      ) || null
    );
  };

  const getRequestedQuantity = (
    form
  ) => {
    const quantityInput =
      form?.querySelector(
        'input[name="quantity"]'
      );

    return Math.max(
      1,
      parseInt(
        quantityInput?.value,
        10
      ) || 1
    );
  };

  const getSafeQuantity = (
    form,
    installationOption
  ) => {
    if (
      !form ||
      !installationOption
    ) {
      return 1;
    }

    const variantId =
      Number(
        form.querySelector(
          'input[name="id"]'
        )?.value
      );

    const requestedQuantity =
      getRequestedQuantity(
        form
      );

    if (!variantId) {
      return requestedQuantity;
    }

    const inventory =
      getVariantInventory(
        installationOption,
        variantId
      );

    /*
     * No inventory information.
     */
    if (!inventory) {
      return requestedQuantity;
    }

    /*
     * Inventory is not tracked.
     */
    if (
      !inventory.inventory_management
    ) {
      return requestedQuantity;
    }

    /*
     * Continue selling when out of stock.
     */
    if (
      String(
        inventory.inventory_policy
      ).toLowerCase() ===
      'continue'
    ) {
      return requestedQuantity;
    }

    const availableQuantity =
      Math.max(
        0,
        Number(
          inventory.inventory_quantity
        ) || 0
      );

    if (
      availableQuantity <= 0
    ) {
      return 0;
    }

    return Math.min(
      requestedQuantity,
      availableQuantity
    );
  };

  const normalizeInstallationQuantity = (
    checkbox
  ) => {
    if (
      !checkbox ||
      !checkbox.checked
    ) {
      return null;
    }

    const installationOption =
      checkbox.closest(
        '[data-qs-installation]'
      );

    const form =
      getProductForm(
        checkbox
      );

    if (
      !installationOption ||
      !form
    ) {
      return null;
    }

    const quantityInput =
      form.querySelector(
        'input[name="quantity"]'
      );

    const requestedQuantity =
      getRequestedQuantity(
        form
      );

    const safeQuantity =
      getSafeQuantity(
        form,
        installationOption
      );

    if (
      safeQuantity <= 0
    ) {
      return {
        requestedQuantity,
        safeQuantity: 0
      };
    }

    /*
     * Customer requested more than stock.
     *
     * Example:
     * Product requested: 2
     * Available: 1
     *
     * Product -> 1
     * Installation -> 1
     */
    if (
      safeQuantity <
        requestedQuantity &&
      quantityInput
    ) {
      quantityInput.value =
        safeQuantity;
    }

    return {
      requestedQuantity,
      safeQuantity
    };
  };

  /* =========================================================
     NATIVE SHOPIFY BUY IT NOW
     ========================================================= */

  const removeAcceleratedInstallationFields =
    (form) => {
      if (!form) return;

      form
        .querySelectorAll(
          '[data-qs-accelerated-installation]'
        )
        .forEach(
          (input) =>
            input.remove()
        );
    };

  const appendAcceleratedField = (
    form,
    name,
    value
  ) => {
    const input =
      document.createElement(
        'input'
      );

    input.type = 'hidden';
    input.name = name;
    input.value =
      String(value);

    input.setAttribute(
      'data-qs-accelerated-installation',
      ''
    );

    form.appendChild(input);
  };

  const syncAcceleratedCheckoutFields =
    (checkbox) => {
      if (!checkbox) return;

      const installationOption =
        checkbox.closest(
          '[data-qs-installation]'
        );

      const form =
        getProductForm(
          checkbox
        );

      if (
        !installationOption ||
        !form
      ) {
        return;
      }

      /*
       * Remove previously generated fields.
       */
      removeAcceleratedInstallationFields(
        form
      );

      /*
       * Installation isn't selected.
       * Native Buy It Now works normally.
       */
      if (!checkbox.checked) {
        return;
      }

      const quantityResult =
        normalizeInstallationQuantity(
          checkbox
        );

      if (
        !quantityResult ||
        quantityResult.safeQuantity <=
          0
      ) {
        return;
      }

      const installationVariantId =
        Number(
          installationOption.dataset
            .installationVariantId
        );

      const mainVariantId =
        Number(
          form.querySelector(
            'input[name="id"]'
          )?.value
        );

      const quantity =
        quantityResult.safeQuantity;

      if (
        !installationVariantId ||
        !mainVariantId
      ) {
        return;
      }

      /*
       * Installation item.
       */
      appendAcceleratedField(
        form,
        'shopify-additional-items[1][id]',
        installationVariantId
      );

      /*
       * Installation quantity matches product.
       */
      appendAcceleratedField(
        form,
        'shopify-additional-items[1][quantity]',
        quantity
      );

      /*
       * Native Shopify parent relationship.
       */
      appendAcceleratedField(
        form,
        'shopify-additional-items[1][parent_id]',
        mainVariantId
      );
    };

  const syncAcceleratedCheckoutForElement =
    (element) => {
      const checkbox =
        getInstallationCheckbox(
          element
        );

      if (!checkbox) return;

      syncAcceleratedCheckoutFields(
        checkbox
      );
    };

  /* =========================================================
     PRODUCT PAGE EVENTS
     ========================================================= */

  document.addEventListener(
    'change',
    (event) => {
      const target =
        event.target;

      if (
        !(target instanceof Element)
      ) {
        return;
      }

      /*
       * Installation selected/unselected.
       */
      if (
        target.matches(
          '[data-qs-installation-checkbox]'
        )
      ) {
        syncAcceleratedCheckoutFields(
          target
        );

        return;
      }

      /*
       * Variant or quantity change.
       */
      if (
        target.matches(
          `${PRODUCT_FORM_SELECTOR} input[name="id"]`
        ) ||
        target.matches(
          `${PRODUCT_FORM_SELECTOR} input[name="quantity"]`
        )
      ) {
        syncAcceleratedCheckoutForElement(
          target
        );
      }
    }
  );

  document.addEventListener(
    'input',
    (event) => {
      const target =
        event.target;

      if (
        !(target instanceof Element)
      ) {
        return;
      }

      if (
        target.matches(
          `${PRODUCT_FORM_SELECTOR} input[name="quantity"]`
        )
      ) {
        syncAcceleratedCheckoutForElement(
          target
        );
      }
    }
  );

  /*
   * Hyper variant change safety.
   */
  document.addEventListener(
    'variant:changed',
    () => {
      window.setTimeout(
        () => {
          document
            .querySelectorAll(
              '[data-qs-installation-checkbox]:checked'
            )
            .forEach(
              (checkbox) => {
                syncAcceleratedCheckoutFields(
                  checkbox
                );
              }
            );
        },
        20
      );
    }
  );

  /*
   * Final check immediately before
   * Shopify Buy It Now.
   */
  document.addEventListener(
    'click',
    (event) => {
      const target =
        event.target;

      if (
        !(target instanceof Element)
      ) {
        return;
      }

      const paymentButton =
        target.closest(
          '.shopify-payment-button button, .shopify-payment-button__button'
        );

      if (!paymentButton) {
        return;
      }

      const checkbox =
        getInstallationCheckbox(
          paymentButton
        );

      if (
        !checkbox ||
        !checkbox.checked
      ) {
        return;
      }

      syncAcceleratedCheckoutFields(
        checkbox
      );
    },
    true
  );

  /*
   * Theme Editor reload safety.
   */
  document.addEventListener(
    'shopify:section:load',
    () => {
      window.setTimeout(
        () => {
          document
            .querySelectorAll(
              '[data-qs-installation-checkbox]:checked'
            )
            .forEach(
              (checkbox) => {
                syncAcceleratedCheckoutFields(
                  checkbox
                );
              }
            );
        },
        50
      );
    }
  );

  /* =========================================================
     ADD TO CART
     ========================================================= */

  document.addEventListener(
    'submit',

    async (event) => {
      const form =
        event.target;

      if (
        !(
          form instanceof
          HTMLFormElement
        )
      ) {
        return;
      }

      if (
        !form.matches(
          PRODUCT_FORM_SELECTOR
        )
      ) {
        return;
      }

      const productRoot =
        getProductRoot(form) ||
        form.parentElement;

      if (!productRoot) {
        return;
      }

      const installationOption =
        productRoot.querySelector(
          '[data-qs-installation]'
        );

      /*
       * Installation isn't available.
       * Hyper handles normal ATC.
       */
      if (!installationOption) {
        return;
      }

      const checkbox =
        installationOption.querySelector(
          '[data-qs-installation-checkbox]'
        );

      /*
       * Installation isn't selected.
       * Hyper handles normal ATC.
       */
      if (
        !checkbox ||
        !checkbox.checked
      ) {
        return;
      }

      /*
       * QSales handles ATC when
       * installation is selected.
       */
      event.preventDefault();
      event.stopImmediatePropagation();

      if (
        form.dataset
          .qsInstallationProcessing ===
        'true'
      ) {
        return;
      }

      form.dataset.qsInstallationProcessing =
        'true';

      clearError(form);

      const submitButton =
        form.querySelector(
          '[name="add"]'
        );

      if (submitButton) {
        submitButton.setAttribute(
          'aria-disabled',
          'true'
        );

        submitButton.classList.add(
          'btn--loading'
        );
      }

      try {
        /*
         * Stock check.
         */
        const quantityResult =
          normalizeInstallationQuantity(
            checkbox
          );

        if (
          !quantityResult ||
          quantityResult.safeQuantity <=
            0
        ) {
          throw new Error(
            'The selected product option is currently out of stock.'
          );
        }

        /*
         * FormData must be generated after
         * quantity normalization.
         */
        const formData =
          new FormData(form);

        const mainVariantId =
          Number(
            formData.get('id')
          );

        const requestedQuantity =
          quantityResult.safeQuantity;

        const installationVariantId =
          Number(
            installationOption.dataset
              .installationVariantId
          );

        const mainProductId =
          installationOption.dataset
            .mainProductId || '';

        const mainProductTitle =
          installationOption.dataset
            .mainProductTitle || '';

        if (!mainVariantId) {
          throw new Error(
            'Please select an available product option.'
          );
        }

        if (
          !installationVariantId
        ) {
          throw new Error(
            'Installation service is currently unavailable.'
          );
        }

        const existingProperties =
          getExistingProperties(
            formData
          );

        const groupId =
          createGroupId();

        /*
         * Main product relationship properties.
         */
        const mainProductProperties =
          {
            ...existingProperties,

            _qs_installation_group:
              groupId,

            _qs_installation_service_variant:
              String(
                installationVariantId
              )
          };

        /*
         * Installation relationship properties.
         */
        const installationProperties =
          {
            _qs_installation_group:
              groupId,

            _qs_parent_product:
              String(
                mainProductId
              ),

            _qs_parent_variant:
              String(
                mainVariantId
              ),

            For:
              mainProductTitle
          };

        const sectionsToBundle =
          getSectionsToBundle();

        /*
         * Installation first, product second.
         *
         * Hyper displays:
         *
         * Main product
         * Installation
         */
        const payload = {
          items: [
            {
              id:
                installationVariantId,

              quantity:
                requestedQuantity,

              properties:
                installationProperties
            },

            {
              id:
                mainVariantId,

              quantity:
                requestedQuantity,

              properties:
                mainProductProperties
            }
          ]
        };

        if (
          sectionsToBundle.length
        ) {
          payload.sections =
            sectionsToBundle;

          payload.sections_url =
            window.location.pathname;
        }

        const routes =
          getRoutes();

        const response =
          await fetch(
            routes.add,
            {
              method: 'POST',

              headers: {
                'Content-Type':
                  'application/json',

                Accept:
                  'application/json',

                'X-Requested-With':
                  'XMLHttpRequest'
              },

              body:
                JSON.stringify(
                  payload
                )
            }
          );

        const result =
          await response.json();

        if (
          !response.ok ||
          result.status
        ) {
          throw new Error(
            result.description ||
              result.message ||
              'Unable to add the product to your cart.'
          );
        }

        /*
         * Reset installation selector.
         */
        checkbox.checked =
          false;

        removeAcceleratedInstallationFields(
          form
        );

        /*
         * Maintain Hyper compatibility.
         */
        document.dispatchEvent(
          new CustomEvent(
            'product-ajax:added',
            {
              detail: {
                product:
                  result
              }
            }
          )
        );

        /*
         * Hyper configured for cart page.
         */
        if (
          window.FoxTheme
            ?.settings
            ?.cartType ===
          'page'
        ) {
          window.location.href =
            routes.cart;

          return;
        }

        /*
         * Refresh/open cart drawer.
         */
        document.dispatchEvent(
          new CustomEvent(
            'cart:refresh',
            {
              bubbles: true,

              detail: {
                open: true
              }
            }
          )
        );
      } catch (error) {
        console.error(
          'QSales Installation System:',
          error
        );

        showError(
          form,
          error.message ||
            'Something went wrong. Please try again.'
        );
      } finally {
        delete form.dataset
          .qsInstallationProcessing;

        if (submitButton) {
          submitButton.removeAttribute(
            'aria-disabled'
          );

          submitButton.classList.remove(
            'btn--loading'
          );
        }
      }
    },

    true
  );

  /* =========================================================
     CART RELATIONSHIP DETECTION
     ========================================================= */

  const buildInstallationUpdates = (
    cart
  ) => {
    if (
      !cart ||
      !Array.isArray(
        cart.items
      )
    ) {
      return {};
    }

    const groups =
      new Map();

    cart.items.forEach(
      (item) => {
        const properties =
          item.properties || {};

        const groupId =
          properties[
            '_qs_installation_group'
          ];

        if (!groupId) {
          return;
        }

        if (
          !groups.has(
            groupId
          )
        ) {
          groups.set(
            groupId,
            {
              main: null,
              installation: null
            }
          );
        }

        const group =
          groups.get(
            groupId
          );

        /*
         * Installation service line.
         */
        if (
          properties[
            '_qs_parent_variant'
          ]
        ) {
          group.installation =
            item;

          return;
        }

        /*
         * Main product line.
         */
        if (
          properties[
            '_qs_installation_service_variant'
          ]
        ) {
          group.main =
            item;
        }
      }
    );

    const updates = {};

    groups.forEach(
      ({
        main,
        installation
      }) => {
        /*
         * Parent removed:
         * remove linked installation.
         */
        if (
          !main &&
          installation
        ) {
          updates[
            installation.key
          ] = 0;

          return;
        }

        /*
         * Installation deliberately removed:
         * keep main product.
         *
         * Do NOT re-add installation.
         */
        if (
          main &&
          !installation
        ) {
          return;
        }

        if (
          !main ||
          !installation
        ) {
          return;
        }

        /*
         * Installation quantity must
         * equal parent quantity.
         */
        if (
          Number(
            installation.quantity
          ) !==
          Number(
            main.quantity
          )
        ) {
          updates[
            installation.key
          ] =
            Number(
              main.quantity
            );
        }
      }
    );

    return updates;
  };

  /* =========================================================
     APPLY INSTALLATION CART UPDATES
     ========================================================= */

  const applyInstallationUpdates =
    async (updates) => {
      const updateKeys =
        Object.keys(
          updates
        );

      if (
        !updateKeys.length ||
        cartSyncInProgress
      ) {
        return;
      }

      cartSyncInProgress =
        true;

      try {
        const routes =
          getRoutes();

        const sections =
          getSectionsToBundle();

        const payload = {
          updates
        };

        if (
          sections.length
        ) {
          payload.sections =
            sections;

          payload.sections_url =
            window.location.pathname;
        }

        const response =
          await fetch(
            routes.update,
            {
              method: 'POST',

              headers: {
                'Content-Type':
                  'application/json',

                Accept:
                  'application/json',

                'X-Requested-With':
                  'XMLHttpRequest'
              },

              body:
                JSON.stringify(
                  payload
                )
            }
          );

        const cart =
          await response.json();

        if (
          !response.ok ||
          cart.status
        ) {
          throw new Error(
            cart.description ||
              cart.message ||
              'Unable to update installation.'
          );
        }

        /*
         * IMPORTANT:
         *
         * No window.location.reload().
         *
         * Hyper updates its cart sections
         * without reloading the entire page.
         */

        if (
          cart.sections &&
          publishCartUpdate(cart)
        ) {
          return;
        }

        /*
         * Fallback if bundled sections or
         * Hyper pubsub aren't available.
         */
        document.dispatchEvent(
          new CustomEvent(
            'cart:refresh',
            {
              bubbles: true,

              detail: {
                open: false
              }
            }
          )
        );
      } catch (error) {
        console.error(
          'QSales Installation Cart Sync:',
          error
        );
      } finally {
        window.setTimeout(
          () => {
            cartSyncInProgress =
              false;
          },
          120
        );
      }
    };

  /* =========================================================
     STANDARD HYPER CART UPDATE
     ========================================================= */

  const scheduleInstallationSync =
    (cart) => {
      if (
        cartSyncInProgress
      ) {
        return;
      }

      window.clearTimeout(
        cartSyncTimer
      );

      cartSyncTimer =
        window.setTimeout(
          () => {
            const updates =
              buildInstallationUpdates(
                cart
              );

            if (
              !Object.keys(
                updates
              ).length
            ) {
              return;
            }

            applyInstallationUpdates(
              updates
            );
          },
          80
        );
    };

  /*
   * Hyper cart update event.
   */
  document.addEventListener(
    'cart:updated',
    (event) => {
      if (
        cartSyncInProgress
      ) {
        return;
      }

      const cart =
        event.detail?.cart;

      if (!cart) {
        return;
      }

      scheduleInstallationSync(
        cart
      );
    }
  );

  /* =========================================================
     FULL /CART PAGE FALLBACK
     ========================================================= */

  const fetchLatestCart =
    async () => {
      try {
        const routes =
          getRoutes();

        const response =
          await fetch(
            routes.cartJson,
            {
              method: 'GET',

              headers: {
                Accept:
                  'application/json',

                'X-Requested-With':
                  'XMLHttpRequest'
              },

              cache:
                'no-store'
            }
          );

        const cart =
          await response.json();

        if (
          !response.ok ||
          cart.status
        ) {
          throw new Error(
            cart.description ||
              cart.message ||
              'Unable to read cart.'
          );
        }

        return cart;
      } catch (error) {
        console.error(
          'QSales Full Cart Read:',
          error
        );

        return null;
      }
    };

  const syncFullCartPage =
    async () => {
      if (
        !isFullCartPage() ||
        cartSyncInProgress
      ) {
        return;
      }

      const cart =
        await fetchLatestCart();

      if (!cart) {
        return;
      }

      const updates =
        buildInstallationUpdates(
          cart
        );

      if (
        !Object.keys(
          updates
        ).length
      ) {
        return;
      }

      await applyInstallationUpdates(
        updates
      );
    };

  const scheduleFullCartPageSync =
    (delay = 700) => {
      if (
        !isFullCartPage()
      ) {
        return;
      }

      window.clearTimeout(
        fullCartSyncTimer
      );

      fullCartSyncTimer =
        window.setTimeout(
          () => {
            syncFullCartPage();
          },
          delay
        );
    };

  /* =========================================================
     FULL CART DOM OBSERVER
     ========================================================= */

  const startFullCartObserver =
    () => {
      if (
        !isFullCartPage()
      ) {
        return;
      }

      const cartArea =
        document.querySelector(
          'cart-items'
        ) ||
        document.querySelector(
          '#main-cart-items'
        ) ||
        document.querySelector(
          '.cart__items'
        ) ||
        document.querySelector(
          'form[action*="/cart"]'
        ) ||
        document.body;

      if (!cartArea) {
        return;
      }

      const observer =
        new MutationObserver(
          (mutations) => {
            if (
              cartSyncInProgress
            ) {
              return;
            }

            const meaningfulChange =
              mutations.some(
                (mutation) =>
                  mutation.type ===
                    'childList' ||
                  mutation.type ===
                    'attributes' ||
                  mutation.type ===
                    'characterData'
              );

            if (
              !meaningfulChange
            ) {
              return;
            }

            /*
             * Give Hyper time to finish its
             * own cart update first.
             */
            scheduleFullCartPageSync(
              700
            );
          }
        );

      observer.observe(
        cartArea,
        {
          subtree: true,

          childList: true,

          characterData: true,

          attributes: true,

          attributeFilter: [
            'value',
            'class',
            'disabled',
            'aria-disabled'
          ]
        }
      );

      /*
       * Backup for quantity fields.
       */
      document.addEventListener(
        'change',
        (event) => {
          if (
            !isFullCartPage()
          ) {
            return;
          }

          const target =
            event.target;

          if (
            !(
              target instanceof
              Element
            )
          ) {
            return;
          }

          if (
            target.closest(
              [
                'cart-items',
                '#main-cart-items',
                '.cart__items',
                'form[action*="/cart"]'
              ].join(',')
            )
          ) {
            scheduleFullCartPageSync(
              700
            );
          }
        }
      );

      /*
       * Backup for:
       * + button
       * - button
       * remove button
       */
      document.addEventListener(
        'click',
        (event) => {
          if (
            !isFullCartPage()
          ) {
            return;
          }

          const target =
            event.target;

          if (
            !(
              target instanceof
              Element
            )
          ) {
            return;
          }

          if (
            target.closest(
              [
                'cart-items',
                '#main-cart-items',
                '.cart__items',
                'form[action*="/cart"]'
              ].join(',')
            )
          ) {
            scheduleFullCartPageSync(
              800
            );
          }
        }
      );
    };

  /* =========================================================
     INITIAL FULL CART SAFETY CHECK
     ========================================================= */

  if (
    isFullCartPage()
  ) {
    window.setTimeout(
      () => {
        /*
         * Repair an existing mismatch
         * when /cart first loads.
         */
        syncFullCartPage();

        /*
         * Then watch future changes.
         */
        startFullCartObserver();
      },
      500
    );
  }
})();